"use client";

import { useState, useMemo } from "react";
import { GroundingSearchItem, RankBand } from "./types";
import { getRankBand, getActiveEngine, getBrandRankForItem, isGapForItem } from "./utils";
import { getGroundingPlatformLogo, getGroundingPlatformDisplayName } from "@repo/core/client";
import { cn } from "@ui/lib";
import { ChevronDown, ChevronUp, Filter, Zap } from "lucide-react";
import Image from "next/image";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@ui/components/select";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@ui/components/tooltip";

interface GroundingSearchHeatMapProps {
  items: GroundingSearchItem[];
  className?: string;
  maxRows?: number;
  showFilters?: boolean;
}

// Rank band configuration with colors
const RANK_BAND_CONFIG: Record<RankBand, { 
  label: string; 
  shortLabel: string;
  bgColor: string; 
  textColor: string;
  borderColor: string;
}> = {
  top3: { 
    label: "Top 3", 
    shortLabel: "1-3",
    bgColor: "bg-emerald-500", 
    textColor: "text-white",
    borderColor: "border-emerald-600"
  },
  top10: { 
    label: "4-10", 
    shortLabel: "4-10",
    bgColor: "bg-green-400", 
    textColor: "text-white",
    borderColor: "border-green-500"
  },
  top20: { 
    label: "11-20", 
    shortLabel: "11-20",
    bgColor: "bg-yellow-400", 
    textColor: "text-yellow-900",
    borderColor: "border-yellow-500"
  },
  top30: { 
    label: "21-30", 
    shortLabel: "21-30",
    bgColor: "bg-orange-400", 
    textColor: "text-white",
    borderColor: "border-orange-500"
  },
  unranked: { 
    label: "Not Ranked", 
    shortLabel: "—",
    bgColor: "bg-gray-200", 
    textColor: "text-gray-500",
    borderColor: "border-gray-300"
  },
};

function PlatformIcons({ platforms }: { platforms?: string[] }) {
  if (!platforms?.length) return null;
  const unique = Array.from(new Set(platforms)).slice(0, 3);
  return (
    <span className="inline-flex items-center gap-0.5" title={unique.map(getGroundingPlatformDisplayName).join(", ")}>
      {unique.map((p) => {
        const src = getGroundingPlatformLogo(p);
        if (!src) return null;
        return (
          <Image key={p} src={src} alt={getGroundingPlatformDisplayName(p)} width={14} height={14} className="rounded-sm" />
        );
      })}
    </span>
  );
}

// Get the best competitor rank for a specific engine
function getBestCompetitorRankForEngine(
  item: GroundingSearchItem,
  engine: "google" | "brave"
): number | null {
  let bestRank: number | null = null;

  item.competitorRankings.forEach(comp => {
    const rank = engine === "google" ? comp.rankGoogle : comp.rankBrave;
    if (rank !== null && (bestRank === null || rank < bestRank)) {
      bestRank = rank;
    }
  });

  return bestRank;
}

// Heat map cell component
function HeatMapCell({
  brandRank,
  bestCompetitorRank,
  impactScore,
  engine,
  queryName,
}: {
  brandRank: number | null;
  bestCompetitorRank: number | null;
  impactScore: number;
  engine: "google" | "brave";
  queryName: string;
}) {
  const brandBand = getRankBand(brandRank);
  const competitorBand = getRankBand(bestCompetitorRank);
  const config = RANK_BAND_CONFIG[brandBand];
  const competitorConfig = RANK_BAND_CONFIG[competitorBand];
  
  // Determine if there's a gap (brand not ranked or ranked worse than competitor)
  const hasGap = brandRank === null && bestCompetitorRank !== null;
  const isBehind = brandRank !== null && bestCompetitorRank !== null && brandRank > bestCompetitorRank;
  
  // Calculate opacity based on impact score (higher = more opaque)
  const impactOpacity = Math.max(0.4, Math.min(1, impactScore / 100));
  
  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <div 
            className={cn(
              "relative w-full h-10 rounded-md flex items-center justify-center text-xs font-medium transition-all cursor-default",
              config.bgColor,
              config.textColor,
              // Add ring if competitor is in a better position
              hasGap && "ring-2 ring-red-500 ring-offset-1",
              isBehind && "ring-2 ring-orange-500 ring-offset-1"
            )}
            style={{ opacity: brandRank === null ? 0.5 : impactOpacity }}
          >
            {/* Brand rank display */}
            {brandRank !== null ? (
              <span className="font-bold">#{brandRank}</span>
            ) : (
              <span className="text-gray-400">—</span>
            )}
            
            {/* Competitor indicator dot */}
            {bestCompetitorRank !== null && (
              <div 
                className={cn(
                  "absolute -top-1 -right-1 w-4 h-4 rounded-full flex items-center justify-center text-[8px] font-bold border-2 border-white shadow-sm",
                  competitorConfig.bgColor,
                  competitorConfig.textColor
                )}
                title={`Competitor #${bestCompetitorRank}`}
              >
                {bestCompetitorRank <= 10 ? bestCompetitorRank : "•"}
              </div>
            )}
            
            {/* High impact indicator */}
            {impactScore >= 70 && brandRank === null && (
              <Zap className="absolute -bottom-1 -right-1 w-3 h-3 text-amber-500 fill-amber-500" />
            )}
          </div>
        </TooltipTrigger>
        <TooltipContent side="top" className="max-w-xs">
          <div className="text-xs space-y-1">
            <p className="font-semibold">{queryName}</p>
            <p>
              <span className="text-gray-500">{engine === "google" ? "Google" : "Brave"}:</span>{" "}
              {brandRank !== null ? `#${brandRank}` : "Not ranked"}
            </p>
            {bestCompetitorRank !== null && (
              <p>
                <span className="text-gray-500">Best competitor:</span> #{bestCompetitorRank}
              </p>
            )}
            <p>
              <span className="text-gray-500">Impact:</span> {Number(impactScore).toFixed(2)}
            </p>
          </div>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

export function GroundingSearchHeatMap({
  items,
  className,
  maxRows = 15,
  showFilters = true,
}: GroundingSearchHeatMapProps) {
  const [sortBy, setSortBy] = useState<"impact" | "query" | "gaps">("impact");
  const [filterBy, setFilterBy] = useState<"all" | "gaps" | "ranked">("all");
  const [showAll, setShowAll] = useState(false);
  
  // Get unique competitors for filtering
  const competitors = useMemo(() => {
    const compMap = new Map<string, { id: string; name: string }>();
    items.forEach(item => {
      item.competitorRankings.forEach(comp => {
        if (!compMap.has(comp.competitorId)) {
          compMap.set(comp.competitorId, { id: comp.competitorId, name: comp.competitorName });
        }
      });
    });
    return Array.from(compMap.values());
  }, [items]);
  
  const [selectedCompetitor, setSelectedCompetitor] = useState<string | null>(null);
  
  // Filter and sort items
  const processedItems = useMemo(() => {
    let filtered = [...items];
    
    // Apply filter
    if (filterBy === "gaps") {
      filtered = filtered.filter(isGapForItem);
    } else if (filterBy === "ranked") {
      filtered = filtered.filter(item => getBrandRankForItem(item) !== null);
    }
    
    // Filter by competitor if selected
    if (selectedCompetitor) {
      filtered = filtered.filter(item => 
        item.competitorRankings.some(c => c.competitorId === selectedCompetitor)
      );
    }
    
    // Sort
    filtered.sort((a, b) => {
      switch (sortBy) {
        case "impact":
          return b.impactScore - a.impactScore;
        case "gaps":
          const aIsGap = isGapForItem(a) ? 1 : 0;
          const bIsGap = isGapForItem(b) ? 1 : 0;
          if (aIsGap !== bIsGap) return bIsGap - aIsGap;
          return b.impactScore - a.impactScore;
        case "query":
          return a.searchQuery.localeCompare(b.searchQuery);
        default:
          return 0;
      }
    });
    
    return filtered;
  }, [items, filterBy, sortBy, selectedCompetitor]);
  
  const displayedItems = showAll ? processedItems : processedItems.slice(0, maxRows);
  const hasMore = processedItems.length > maxRows;
  
  // Summary stats
  const stats = useMemo(() => {
    const gapCount = items.filter(isGapForItem).length;
    const rankedCount = items.filter(i => getBrandRankForItem(i) !== null).length;
    const highImpactGaps = items.filter(i => isGapForItem(i) && i.impactScore >= 50).length;
    return { gapCount, rankedCount, highImpactGaps };
  }, [items]);

  if (items.length === 0) {
    return null;
  }

  return (
    <div className={cn("space-y-4", className)}>
      {/* Compact Filter Bar */}
      {showFilters && (
        <div className="flex flex-wrap items-center gap-3 text-sm">
          <div className="flex items-center gap-2">
            <Filter className="w-4 h-4 text-gray-400" />
            <Select value={filterBy} onValueChange={(v) => setFilterBy(v as typeof filterBy)}>
              <SelectTrigger className="h-8 w-28 text-xs">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All ({items.length})</SelectItem>
                <SelectItem value="gaps">Gaps ({stats.gapCount})</SelectItem>
                <SelectItem value="ranked">Ranked ({stats.rankedCount})</SelectItem>
              </SelectContent>
            </Select>
          </div>
          
          <Select value={sortBy} onValueChange={(v) => setSortBy(v as typeof sortBy)}>
            <SelectTrigger className="h-8 w-28 text-xs">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="impact">By Impact</SelectItem>
              <SelectItem value="gaps">Gaps First</SelectItem>
              <SelectItem value="query">A-Z</SelectItem>
            </SelectContent>
          </Select>
          
          {competitors.length > 0 && (
            <Select 
              value={selectedCompetitor || "all"} 
              onValueChange={(v) => setSelectedCompetitor(v === "all" ? null : v)}
            >
              <SelectTrigger className="h-8 w-36 text-xs">
                <SelectValue placeholder="All competitors" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All competitors</SelectItem>
                {competitors.map(comp => (
                  <SelectItem key={comp.id} value={comp.id}>{comp.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        </div>
      )}
      
      {/* Legend */}
      <div className="flex flex-wrap items-center gap-4 text-xs text-gray-600">
        <span className="font-medium">Your rank:</span>
        {Object.entries(RANK_BAND_CONFIG).map(([key, config]) => (
          <div key={key} className="flex items-center gap-1">
            <div className={cn("w-4 h-4 rounded", config.bgColor)} />
            <span>{config.label}</span>
          </div>
        ))}
        <span className="border-l pl-4 ml-2">
          <span className="inline-flex items-center gap-1">
            <div className="w-3 h-3 rounded-full bg-emerald-500 border-2 border-white shadow" />
            = Competitor rank
          </span>
        </span>
      </div>
      
      {/* Heat Map Grid */}
      <div className="border rounded-lg overflow-hidden">
        {/* Header */}
        <div className="grid grid-cols-[48px_minmax(0,1fr)_80px_60px] bg-gray-50 border-b text-xs font-medium text-gray-600">
          <div className="px-1 py-2 flex items-center justify-center" title="Source platform">
            Source
          </div>
          <div className="px-3 py-2 border-l">Search Query</div>
          <div className="px-2 py-2 text-center border-l">
            <span className="inline-flex items-center gap-1" title="Google for ChatGPT/Gemini grounding, Brave for Claude grounding">
              <Image src="/images/google-logo.svg" alt="Google" width={16} height={16} />
              <Image src="/images/brave-logo.svg" alt="Brave" width={16} height={16} />
              Rank
            </span>
          </div>
          <div className="px-2 py-2 text-center border-l">
            <Zap className="w-3 h-3 inline text-amber-500" />
          </div>
        </div>
        
        {/* Rows */}
        <div className="divide-y">
          {displayedItems.map((item) => {
            const engine = getActiveEngine(item);
            const bestCompetitorRank = getBestCompetitorRankForEngine(item, engine);

            return (
              <div 
                key={item.id}
                className="grid grid-cols-[48px_minmax(0,1fr)_80px_60px] hover:bg-gray-50 transition-colors"
              >
                {/* Platform icons */}
                <div className="px-1 py-2 flex items-center justify-center">
                  <PlatformIcons platforms={item.platforms} />
                </div>
                
                {/* Query */}
                <div className="px-3 py-2 flex items-start min-w-0 border-l">
                  <span className="text-sm text-gray-900 break-words" title={item.searchQuery}>
                    {item.searchQuery}
                  </span>
                  {item.usageCount > 1 && (
                    <span className="ml-2 text-xs text-gray-400 flex-shrink-0">
                      ×{item.usageCount}
                    </span>
                  )}
                </div>
                
                {/* Rank cell (Google for ChatGPT/Gemini, Brave for Claude) */}
                <div className="px-2 py-1.5 border-l">
                  <HeatMapCell
                    brandRank={getBrandRankForItem(item)}
                    bestCompetitorRank={bestCompetitorRank}
                    impactScore={item.impactScore}
                    engine={engine}
                    queryName={item.searchQuery}
                  />
                </div>
                
                {/* Impact score */}
                <div className="px-2 py-2 border-l flex items-center justify-center">
                  <span 
                    className={cn(
                      "text-xs font-medium px-1.5 py-0.5 rounded",
                      item.impactScore >= 70 ? "bg-red-100 text-red-700" :
                      item.impactScore >= 40 ? "bg-amber-100 text-amber-700" :
                      "bg-gray-100 text-gray-600"
                    )}
                  >
                    {Number(item.impactScore).toFixed(2)}
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      </div>
      
      {/* Show More/Less */}
      {hasMore && (
        <button
          onClick={() => setShowAll(!showAll)}
          className="flex items-center gap-1 text-sm text-blue-600 hover:text-blue-800 font-medium mx-auto"
        >
          {showAll ? (
            <>
              <ChevronUp className="w-4 h-4" />
              Show less
            </>
          ) : (
            <>
              <ChevronDown className="w-4 h-4" />
              Show all {processedItems.length} searches
            </>
          )}
        </button>
      )}
    </div>
  );
}

